/**
 * PublicDataTable — premium data table for public views.
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../theme/ThemeContext';
import PublicFieldValue from './PublicFieldValue';
import JsonCell from '../common/JsonCell';
import { getCurrencyDisplay } from '../../utils/currencies';
import { zoneOf } from '../../utils/utils';

// Per-type column configuration: preferred width (px) and optional char truncation limit.
const COL_CONFIG = {
  boolean: { width: 90 },
  integer: { width: 100 },
  float: { width: 110 },
  decimal: { width: 110 },
  numeric: { width: 110 },
  date: { width: 130 },
  datetime: { width: 180 },
  image: { width: 90 },
  file: { width: 120 },
  enum: { width: 140 },
  fk: { width: 160, maxChars: 28 },
  one_to_one: { width: 160, maxChars: 28 },
  one_to_many: { width: 160, maxChars: 28 },
  string: { width: 180, maxChars: 38 },
  text: { width: 220, maxChars: 55 },
};

const TRUNCATE_TYPES = new Set(['string', 'text', 'fk', 'one_to_one', 'one_to_many']);

function toRawString(value) {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Blend a hex accent color with white at the given alpha (0–1).
 * Returns a fully opaque rgb() value — required for sticky columns so
 * content scrolling beneath them cannot bleed through.
 */
function blendWithWhite(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgb(${Math.round(255 + (r - 255) * alpha)},${Math.round(255 + (g - 255) * alpha)},${Math.round(255 + (b - 255) * alpha)})`;
}

export default function PublicDataTable({ records = [], fields = [], mapping = {}, onSort, sortField, sortDir, detailEnabled = true, detailButtonLabel = '', tableSlug: tableSlugProp }) {
  const navigate = useNavigate();
  const { tableSlug: tableSlugParam } = useParams();
  const tableSlug = tableSlugProp || tableSlugParam;
  const { theme } = useTheme();
  const accentColor = theme.primary;

  const actionColWidth = detailButtonLabel ? 130 : 64;

  // Solid backgrounds for sticky column — semi-transparent values bleed when
  // other columns scroll behind the stuck cell.
  const evenRowBg = blendWithWhite(accentColor, 0.08);
  const hoverRowBg = blendWithWhite(accentColor, 0.15);
  const headerBg = blendWithWhite(accentColor, 0.22);

  const css = `
    .pdt-wrapper {
      overflow-x: auto;
      max-height: 600px;
      background:
        linear-gradient(white 30%, rgba(255,255,255,0)) center top,
        linear-gradient(rgba(255,255,255,0), white 70%) center bottom,
        radial-gradient(farthest-side at 50% 0, rgba(0,0,0,0.12), rgba(0,0,0,0)) center top,
        radial-gradient(farthest-side at 50% 100%, rgba(0,0,0,0.12), rgba(0,0,0,0)) center bottom;
      background-repeat: no-repeat;
      background-size: 100% 60px, 100% 60px, 100% 16px, 100% 16px;
      background-attachment: local, local, scroll, scroll;
    }

    .pdt-th {
      font-weight: 700;
      font-size: 0.85rem;
      color: ${accentColor};
      background: ${headerBg};
      border-bottom: 2px solid ${accentColor}40;
      text-align: center;
      white-space: nowrap;
      padding: 0.95rem 0.75rem;
      letter-spacing: 0.07em;
      text-transform: uppercase;
      transition: color 150ms ease, background 150ms ease;
      user-select: none;
    }
    .pdt-th:hover {
      background: ${accentColor}0e !important;
      cursor: pointer;
    }

    /* ── Stripe: apply background on each <td> so sticky cells are covered ── */
    .pdt-row-odd  > td { background: #ffffff; }
    .pdt-row-even > td { background: ${evenRowBg}; }

    /* ── Hover: targets every td in the row including the sticky one ── */
    .pdt-row > td { transition: background 0.15s ease; }
    .pdt-row:hover > td { background: ${hoverRowBg} !important; }

    .pdt-row:active { transform: scaleY(0.995); }

    .pdt-action-btn {
      width: 30px;
      height: 30px;
      border-radius: 50%;
      background: ${accentColor}10;
      border: 1.5px solid ${accentColor}30;
      color: ${accentColor};
      font-size: 0.82rem;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      margin: 0 auto;
      transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease, color 0.15s ease;
    }
    .pdt-action-btn:hover {
      background: ${accentColor};
      border-color: ${accentColor};
      color: #fff;
      transform: scale(1.15);
    }
  `;

  const stickyActionTh = {
    position: 'sticky', right: 0, zIndex: 3,
    background: headerBg,   // solid — no bleed when scrolling horizontally
    borderBottom: `2px solid ${accentColor}28`,
    boxShadow: '-3px 0 12px rgba(0,0,0,0.06)',
    width: actionColWidth, minWidth: actionColWidth,
  };

  // No inline background on sticky td — CSS class (.pdt-row-even/odd > td) handles it,
  // ensuring the sticky cell always matches the row stripe and hover state.
  const stickyActionTd = {
    position: 'sticky', right: 0, zIndex: 2,
    boxShadow: '-3px 0 12px rgba(0,0,0,0.06)',
    textAlign: 'center', verticalAlign: 'middle',
  };

  if (records.length === 0) {
    return (
      <>
        <style>{css}</style>
        <div style={{
          border: `1.5px solid ${accentColor}30`,
          borderRadius: 16, background: '#fff',
          boxShadow: `0 4px 28px ${accentColor}1e, 0 2px 10px rgba(0,0,0,0.07)`,
          padding: '56px 0', textAlign: 'center',
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: `linear-gradient(135deg, ${accentColor}14 0%, ${accentColor}28 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 14px',
            boxShadow: `0 4px 16px ${accentColor}1c`,
          }}>
            <i className="bi bi-table" style={{ fontSize: 26, color: accentColor, opacity: 0.7 }} />
          </div>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', marginBottom: 6 }}>No records found</div>
          <p style={{ color: '#94a3b8', fontSize: '0.84rem', margin: 0, fontWeight: 400 }}>Try adjusting your filters</p>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{css}</style>
      <div
        className="arcee-pub--table"
        style={{
          borderRadius: 'var(--card-radius, 16px)',
          overflow: 'hidden',
          border: `1.5px solid ${accentColor}30`,
          boxShadow: `0 4px 28px ${accentColor}24, 0 2px 12px rgba(0,0,0,0.08)`,
          background: '#ffffff',
        }}
      >
        <div className="pdt-wrapper">
          <table className="table mb-0 align-middle" style={{ minWidth: 500 }}>
            <thead style={{
              position: 'sticky', top: 0, zIndex: 4,
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
            }}>
              <tr style={{
                background: headerBg,
                boxShadow: '0 3px 12px rgba(0,0,0,0.08)',
              }}>
                {fields.map((field) => {
                  const cfg = COL_CONFIG[field.data_type] || { width: 160 };
                  const isSorted = sortField === field.name;
                  return (
                    <th
                      key={field.name}
                      className="pdt-th arcee-pub__th"
                      data-field={field.name}
                      style={{
                        color: accentColor,
                        background: headerBg,
                        width: cfg.width, minWidth: cfg.width,
                        cursor: onSort ? 'pointer' : 'default',
                      }}
                      onClick={onSort ? () => onSort(field.name) : undefined}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {field.display_name}
                        {onSort && (
                          <i
                            className={`bi bi-${isSorted ? (sortDir === 'desc' ? 'sort-down-alt' : 'sort-up-alt') : 'chevron-expand'}`}
                            style={{ fontSize: '0.68rem', opacity: isSorted ? 1 : 0.25, transition: 'opacity 150ms ease' }}
                          />
                        )}
                      </span>
                    </th>
                  );
                })}
                {detailEnabled && <th style={stickyActionTh} />}
              </tr>
            </thead>
            <tbody>
              {records.map((record, rowIdx) => {
                const isEven = rowIdx % 2 === 1;
                return (
                  <tr
                    key={record.id}
                    data-record-id={record.id}
                    className={`pdt-row ${isEven ? 'pdt-row-even' : 'pdt-row-odd'}`}
                    style={{
                      borderBottom: rowIdx < records.length - 1 ? `1px solid ${accentColor}18` : 'none',
                    }}
                  >
                    {fields.map((field) => {
                      const value = record[field.name];
                      const cfg = COL_CONFIG[field.data_type] || { width: 160 };
                      const rawStr = toRawString(value);
                      const shouldTruncate = TRUNCATE_TYPES.has(field.data_type);
                      const limit = cfg.maxChars;
                      const displayStr = shouldTruncate && limit && rawStr.length > limit
                        ? rawStr.slice(0, limit) + '…'
                        : rawStr;
                      const isCostField = mapping.cost_field && field.name === mapping.cost_field;
                      const currencySymbol = isCostField ? getCurrencyDisplay(mapping.cost_currency) : undefined;

                      return (
                        <td
                          key={field.name}
                          className="arcee-pub__td"
                          data-field={field.name}
                          style={{
                            textAlign: 'center',
                            fontSize: '0.87rem', color: '#374151',
                            width: cfg.width, maxWidth: cfg.width,
                            padding: '0.8rem 0.75rem',
                          }}
                          title={shouldTruncate && rawStr ? rawStr : undefined}
                        >
                          {(field.data_type === 'json' || field.data_type === 'jsonb')
                            ? (value === null || value === undefined || value === '' ? '-' : <JsonCell value={value} />)
                            : (shouldTruncate ? displayStr : <PublicFieldValue field={field} value={value} currencySymbol={currencySymbol} timeZone={zoneOf(record)} />)}
                        </td>
                      );
                    })}
                    {detailEnabled && (
                      <td style={stickyActionTd}>
                        {detailButtonLabel ? (
                          <button
                            onClick={e => { e.stopPropagation(); navigate(`/p/${tableSlug}/${record.id}`); }}
                            style={{
                              background: accentColor, color: '#fff', border: 'none',
                              borderRadius: 999, padding: '4px 14px',
                              fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              whiteSpace: 'nowrap',
                              boxShadow: `0 2px 8px ${accentColor}44`,
                              transition: 'opacity 0.15s ease, transform 0.15s ease',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; e.currentTarget.style.transform = 'scale(1.04)'; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1)'; }}
                          >
                            {detailButtonLabel}
                            <i className="bi bi-arrow-right" style={{ fontSize: '0.6rem' }} />
                          </button>
                        ) : (
                          <button
                            className="pdt-action-btn"
                            onClick={e => { e.stopPropagation(); navigate(`/p/${tableSlug}/${record.id}`); }}
                          >
                            <i className="bi bi-arrow-right" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
